import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface RegistrationContent {
  id: string;
  content_key: string;
  content_type: 'text' | 'number' | 'array' | 'object';
  content_value: any;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function RegistrationContentContainer() {
  const [contents, setContents] = useState<RegistrationContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingContent, setEditingContent] = useState<RegistrationContent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    content_key: '',
    content_type: 'text' as const,
    content_value: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchContents();
  }, []);

  const fetchContents = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('registration_content')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setContents(data || []);
    } catch (error) {
      console.error('Error fetching registration content:', error);
      toast.error('Failed to load registration content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (content: RegistrationContent) => {
    console.log('Editing content:', content);
    setEditingContent(content);

    // Convert content_value to string for editing
    let valueString = '';
    if (typeof content.content_value === 'string') {
      valueString = content.content_value;
    } else {
      valueString = JSON.stringify(content.content_value, null, 2);
    }

    console.log('Setting form data with value:', valueString);

    setFormData({
      content_key: content.content_key,
      content_type: content.content_type,
      content_value: valueString,
      display_order: content.display_order,
      is_active: content.is_active
    });
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingContent(null);
    setFormData({
      content_key: '',
      content_type: 'text',
      content_value: '',
      display_order: contents.length + 1,
      is_active: true
    });
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Check authentication
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Current session:', session ? 'Authenticated' : 'Not authenticated');

      if (!session) {
        toast.error('You must be logged in to save content');
        return;
      }

      let parsedValue;
      try {
        parsedValue = JSON.parse(formData.content_value);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        parsedValue = formData.content_value;
      }

      console.log('Saving data:', {
        content_key: formData.content_key,
        content_type: formData.content_type,
        content_value: parsedValue,
        display_order: formData.display_order,
        is_active: formData.is_active
      });

      const dataToSave = {
        content_key: formData.content_key,
        content_type: formData.content_type,
        content_value: parsedValue,
        display_order: formData.display_order,
        is_active: formData.is_active,
        updated_at: new Date().toISOString()
      };

      if (editingContent) {
        console.log('Updating content with ID:', editingContent.id);
        const { data, error } = await supabase
          .from('registration_content')
          .update(dataToSave)
          .eq('id', editingContent.id)
          .select();

        console.log('Update result:', { data, error });

        if (error) throw error;
        toast.success('Content updated successfully');
      } else {
        console.log('Inserting new content');
        const { data, error } = await supabase
          .from('registration_content')
          .insert([dataToSave])
          .select();

        console.log('Insert result:', { data, error });

        if (error) throw error;
        toast.success('Content added successfully');
      }

      setIsModalOpen(false);
      await fetchContents();
    } catch (error: any) {
      console.error('Error saving content:', error);
      toast.error(error.message || 'Failed to save content');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this content?')) return;

    try {
      const { error } = await supabase
        .from('registration_content')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Content deleted successfully');
      fetchContents();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    }
  };

  const toggleActive = async (content: RegistrationContent) => {
    try {
      const { error } = await supabase
        .from('registration_content')
        .update({ is_active: !content.is_active })
        .eq('id', content.id);

      if (error) throw error;
      toast.success(`Content ${!content.is_active ? 'activated' : 'deactivated'}`);
      fetchContents();
    } catch (error) {
      console.error('Error toggling content:', error);
      toast.error('Failed to update content status');
    }
  };

  const renderContentValue = (content: RegistrationContent) => {
    const value = content.content_value;

    if (content.content_type === 'text') {
      return <p className="text-sm text-gray-600">{value}</p>;
    }

    if (content.content_type === 'number') {
      return <p className="text-sm text-gray-600">{value}</p>;
    }

    if (content.content_type === 'array') {
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        return (
          <div className="text-sm text-gray-600">
            {value.map((item, idx) => (
              <div key={idx} className="mb-2">
                <strong>{item.title || `Item ${idx + 1}`}:</strong> {item.description || JSON.stringify(item)}
              </div>
            ))}
          </div>
        );
      }
      return (
        <ul className="text-sm text-gray-600 list-disc list-inside">
          {Array.isArray(value) && value.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      );
    }

    if (content.content_type === 'object') {
      return (
        <div className="text-sm text-gray-600">
          {Object.entries(value).map(([key, val]) => (
            <div key={key}>
              <strong>{key}:</strong> {JSON.stringify(val)}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  const getContentKeyLabel = (key: string) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-display font-bold text-golf-gray">
            Registration Page Content
          </h1>
          <p className="text-gray-600 mt-2">
            Manage all content displayed on the Registration page
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="modern-button px-4 py-2 text-white rounded-lg flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Add Content
        </button>
      </div>

      <div className="grid gap-6">
        {contents.map((content) => (
          <div
            key={content.id}
            className={`bg-white rounded-lg shadow-md p-6 ${
              !content.is_active ? 'opacity-60' : ''
            }`}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-bold text-golf-gray">
                    {getContentKeyLabel(content.content_key)}
                  </h3>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-golf-sand text-golf-gray">
                    {content.content_type}
                  </span>
                  <span className="text-sm text-gray-500">
                    Order: {content.display_order}
                  </span>
                </div>
                <div className="mt-3">{renderContentValue(content)}</div>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => toggleActive(content)}
                  className={`px-3 py-1 text-sm rounded ${
                    content.is_active
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {content.is_active ? 'Active' : 'Inactive'}
                </button>
                <button
                  onClick={() => handleEdit(content)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(content.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-golf-gray mb-6">
                {editingContent ? 'Edit Content' : 'Add Content'}
              </h2>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Key
                  </label>
                  <input
                    type="text"
                    value={formData.content_key}
                    onChange={(e) =>
                      setFormData({ ...formData, content_key: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
                    required
                    disabled={!!editingContent}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use lowercase with underscores (e.g., registration_fee)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Type
                  </label>
                  <select
                    value={formData.content_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        content_type: e.target.value as any
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="array">Array</option>
                    <option value="object">Object</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Value
                  </label>
                  <textarea
                    value={formData.content_value}
                    onChange={(e) =>
                      setFormData({ ...formData, content_value: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent font-mono text-sm"
                    rows={10}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    For arrays and objects, use JSON format
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        display_order: parseInt(e.target.value)
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
                    required
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="w-4 h-4 text-golf-orange border-gray-300 rounded focus:ring-golf-orange"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    Active
                  </label>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 modern-button px-6 py-2 text-white rounded-lg"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
